import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './Invoices.css';

const formatMoney = (n) => (Number(n) || 0).toFixed(2);

/** Puede anularse: no enviada/aceptada en DIAN ni en flujo operativo sent_to_dian/accepted */
const canVoidInvoice = (inv) => {
  if (!inv) return false;
  if (inv.status === 'voided') return false;
  if (inv.status && ['sent_to_dian', 'accepted'].includes(inv.status)) return false;
  if (inv.dianStatus && ['enviada', 'aceptada'].includes(inv.dianStatus)) return false;
  return true;
};

const Invoices = () => {
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [voidModalInvoice, setVoidModalInvoice] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState({
    invoiceType: 'venta',
    thirdPartyId: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    lines: [
      { productCode: '', description: '', quantity: 1, unitPrice: 0, discountPercentage: 0, taxPercentage: 19, accountId: '' }
    ]
  });

  // Obtener facturas
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', companyId, filterType, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterType) params.append('invoiceType', filterType);
      if (filterStatus) params.append('status', filterStatus);
      const response = await api.get(`/companies/${companyId}/invoices?${params}`);
      return response.data.data;
    },
    enabled: !!companyId
  });

  // Obtener terceros para el selector
  const { data: thirdParties } = useQuery({
    queryKey: ['thirdParties', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/third-parties`);
      return response.data.data.thirdParties || [];
    },
    enabled: !!companyId
  });

  // Obtener plan de cuentas (para selector de cuentas en líneas - futuro)
  // eslint-disable-next-line no-unused-vars
  const { data: chartOfAccounts } = useQuery({
    queryKey: ['chartOfAccounts', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/chart-of-accounts`);
      return response.data.data.flat || [];
    },
    enabled: !!companyId
  });

  // Crear factura
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/companies/${companyId}/invoices`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['invoices', companyId]);
      setShowForm(false);
      setFormData({
        invoiceType: 'venta',
        thirdPartyId: '',
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        lines: [
          { productCode: '', description: '', quantity: 1, unitPrice: 0, discountPercentage: 0, taxPercentage: 19, accountId: '' }
        ]
      });
    }
  });

  // Detalle (modal "Ver")
  const {
    data: invoiceDetail,
    isLoading: detailLoading,
    error: detailError
  } = useQuery({
    queryKey: ['invoice', companyId, selectedInvoice?.id],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/invoices/${selectedInvoice.id}`);
      return response.data.data?.invoice;
    },
    enabled: !!companyId && !!selectedInvoice?.id
  });

  // Aprobar factura
  const approveMutation = useMutation({
    mutationFn: async (invoiceId) => {
      const response = await api.post(`/companies/${companyId}/invoices/${invoiceId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['invoices', companyId]);
      queryClient.invalidateQueries({ queryKey: ['invoice', companyId, selectedInvoice?.id] });
      alert('Factura aprobada exitosamente');
      setSelectedInvoice(null);
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || err?.message || 'Error al aprobar';
      alert(msg);
    }
  });

  // Enviar a DIAN
  const sendToDIANMutation = useMutation({
    mutationFn: async (invoiceId) => {
      const response = await api.post(`/companies/${companyId}/invoices/${invoiceId}/send-to-dian`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['invoices', companyId]);
      alert('Factura enviada a DIAN exitosamente');
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || err?.message || 'Error al enviar a DIAN';
      alert(msg);
    }
  });

  const voidMutation = useMutation({
    mutationFn: async ({ id, reason }) => {
      const response = await api.post(
        `/companies/${companyId}/invoices/${id}/void`,
        { reason }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      const id = variables.id;
      queryClient.invalidateQueries(['invoices', companyId]);
      queryClient.invalidateQueries({ queryKey: ['invoice', companyId, id] });
      alert('Factura anulada correctamente');
      setVoidModalInvoice(null);
      setVoidReason('');
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || err?.message || 'Error al anular la factura';
      alert(msg);
    }
  });

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { productCode: '', description: '', quantity: 1, unitPrice: 0, discountPercentage: 0, taxPercentage: 19, accountId: '' }]
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length > 1) {
      setFormData({
        ...formData,
        lines: formData.lines.filter((_, i) => i !== index)
      });
    }
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setFormData({ ...formData, lines: newLines });
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let taxAmount = 0;
    let discountAmount = 0;

    formData.lines.forEach(line => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unitPrice || 0);
      const discountPercent = parseFloat(line.discountPercentage || 0);
      const taxPercent = parseFloat(line.taxPercentage || 0);

      const lineSubtotalBeforeDiscount = quantity * unitPrice;
      const lineDiscount = lineSubtotalBeforeDiscount * (discountPercent / 100);
      const lineSubtotal = lineSubtotalBeforeDiscount - lineDiscount;
      const lineTax = lineSubtotal * (taxPercent / 100);

      subtotal += lineSubtotal;
      taxAmount += lineTax;
      discountAmount += lineDiscount;
    });

    return { subtotal, taxAmount, discountAmount, total: subtotal + taxAmount };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate({
      invoiceType: formData.invoiceType,
      thirdPartyId: formData.thirdPartyId,
      issueDate: formData.issueDate,
      dueDate: formData.dueDate || null,
      lines: formData.lines.map(line => ({
        productCode: line.productCode,
        description: line.description,
        quantity: parseFloat(line.quantity),
        unitPrice: parseFloat(line.unitPrice),
        discountPercentage: parseFloat(line.discountPercentage || 0),
        taxPercentage: parseFloat(line.taxPercentage || 0),
        accountId: line.accountId || null
      }))
    });
  };

  const totals = calculateTotals();

  const apiErrorMessage =
    error?.response?.data?.message ||
    error?.message ||
    'Error al cargar facturas';

  if (isLoading) return <div>Cargando facturas...</div>;
  if (error) {
    return (
      <div className="invoices invoices-error-state">
        <h2>Facturas</h2>
        <p className="invoices-error-text">{apiErrorMessage}</p>
        {error?.response?.status != null && (
          <p className="invoices-error-meta">Código HTTP: {error.response.status}</p>
        )}
        <p className="invoices-error-hint">
          Si el dashboard muestra pendientes pero aquí falla, suele deberse a que la ruta PHP de
          facturas no está desplegada, a un error SQL (tabla/columna) o a permisos del API (403).
        </p>
      </div>
    );
  }

  return (
    <div className="invoices">
      <div className="page-header">
        <h2>Facturas</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : '+ Nueva Factura'}
        </button>
      </div>

      {/* Filtros */}
      <div className="filters-section">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="filter-select"
        >
          <option value="">Todos los tipos</option>
          <option value="venta">Venta</option>
          <option value="compra">Compra</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="enviada">Enviada</option>
          <option value="aceptada">Aceptada</option>
          <option value="rechazada">Rechazada</option>
        </select>
      </div>

      {showForm && (
        <div className="invoice-form-card">
          <h3>Nueva Factura</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Tipo *</label>
                <select
                  value={formData.invoiceType}
                  onChange={(e) => setFormData({ ...formData, invoiceType: e.target.value })}
                  required
                >
                  <option value="venta">Venta</option>
                  <option value="compra">Compra</option>
                </select>
              </div>
              <div className="form-group">
                <label>Tercero *</label>
                <select
                  value={formData.thirdPartyId}
                  onChange={(e) => setFormData({ ...formData, thirdPartyId: e.target.value })}
                  required
                >
                  <option value="">Seleccionar...</option>
                  {thirdParties?.map(tp => (
                    <option key={tp.id} value={tp.id}>
                      {tp.nit} - {tp.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Fecha de Emisión *</label>
                <input
                  type="date"
                  value={formData.issueDate}
                  onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Fecha de Vencimiento</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                />
              </div>
            </div>

            <div className="lines-section">
              <div className="lines-header">
                <h4>Líneas de Factura</h4>
                <button type="button" onClick={addLine} className="btn-secondary">
                  + Agregar Línea
                </button>
              </div>

              <table className="lines-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Descripción</th>
                    <th>Cantidad</th>
                    <th>Precio Unit.</th>
                    <th>Desc. %</th>
                    <th>IVA %</th>
                    <th>Subtotal</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.lines.map((line, index) => {
                    const quantity = parseFloat(line.quantity || 0);
                    const unitPrice = parseFloat(line.unitPrice || 0);
                    const discountPercent = parseFloat(line.discountPercentage || 0);
                    const taxPercent = parseFloat(line.taxPercentage || 0);
                    const lineSubtotalBeforeDiscount = quantity * unitPrice;
                    const lineDiscount = lineSubtotalBeforeDiscount * (discountPercent / 100);
                    const lineSubtotal = lineSubtotalBeforeDiscount - lineDiscount;
                    const lineTax = lineSubtotal * (taxPercent / 100);
                    const lineTotal = lineSubtotal + lineTax;

                    return (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={line.productCode}
                            onChange={(e) => updateLine(index, 'productCode', e.target.value)}
                            placeholder="Código"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) => updateLine(index, 'description', e.target.value)}
                            required
                            placeholder="Descripción"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.quantity}
                            onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                            required
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.unitPrice}
                            onChange={(e) => updateLine(index, 'unitPrice', e.target.value)}
                            required
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={line.discountPercentage}
                            onChange={(e) => updateLine(index, 'discountPercentage', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={line.taxPercentage}
                            onChange={(e) => updateLine(index, 'taxPercentage', e.target.value)}
                          />
                        </td>
                        <td>{lineSubtotal.toFixed(2)}</td>
                        <td>{lineTotal.toFixed(2)}</td>
                        <td>
                          {formData.lines.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLine(index)}
                              className="btn-danger"
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td colSpan="6"><strong>Totales</strong></td>
                    <td><strong>{totals.subtotal.toFixed(2)}</strong></td>
                    <td><strong>{totals.total.toFixed(2)}</strong></td>
                    <td></td>
                  </tr>
                  <tr className="totals-row">
                    <td colSpan="6"><strong>Descuento Total</strong></td>
                    <td colSpan="2"><strong>{totals.discountAmount.toFixed(2)}</strong></td>
                    <td></td>
                  </tr>
                  <tr className="totals-row">
                    <td colSpan="6"><strong>IVA Total</strong></td>
                    <td colSpan="2"><strong>{totals.taxAmount.toFixed(2)}</strong></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn-primary" 
                disabled={createMutation.isLoading}
              >
                {createMutation.isLoading ? 'Creando...' : 'Crear Factura'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="invoices-list">
        <h3>Lista de Facturas ({data?.pagination?.total || 0})</h3>
        {data?.invoices && data.invoices.length > 0 ? (
          <table className="invoices-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Tipo</th>
                <th>Tercero</th>
                <th>Fecha</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map(invoice => (
                <tr key={invoice.id}>
                  <td>{invoice.invoiceNumber}</td>
                  <td>
                    <span className={`type-badge type-${invoice.invoiceType}`}>
                      {invoice.invoiceType}
                    </span>
                  </td>
                  <td>{invoice.thirdParty?.name}</td>
                  <td>{new Date(invoice.issueDate).toLocaleDateString()}</td>
                  <td>${formatMoney(invoice.total)}</td>
                  <td>
                    <div className="invoice-status-badges">
                      {invoice.status === 'voided' && (
                        <span className="status-badge status-voided">Anulada</span>
                      )}
                      <span className={`status-badge status-${invoice.dianStatus}`}>
                        {invoice.dianStatus}
                      </span>
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => setSelectedInvoice(invoice)}
                      className="btn-link"
                    >
                      Ver
                    </button>
                    {invoice.status !== 'voided' && invoice.requiresApproval
                      && !invoice.approvedBy
                      && !invoice.approvedAt
                      && (
                      <button
                        type="button"
                        onClick={() => approveMutation.mutate(invoice.id)}
                        className="btn-link"
                        disabled={approveMutation.isLoading}
                      >
                        Aprobar
                      </button>
                    )}
                    {invoice.status !== 'voided' && (invoice.approvedBy || invoice.approvedAt)
                      && invoice.dianStatus === 'pendiente' && (
                      <button
                        type="button"
                        onClick={() => sendToDIANMutation.mutate(invoice.id)}
                        className="btn-link"
                        disabled={sendToDIANMutation.isLoading}
                      >
                        Enviar DIAN
                      </button>
                    )}
                    {canVoidInvoice(invoice) && (
                      <button
                        type="button"
                        onClick={() => {
                          setVoidModalInvoice(invoice);
                          setVoidReason('');
                        }}
                        className="btn-link btn-void"
                        disabled={voidMutation.isLoading}
                      >
                        Anular
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>No hay facturas registradas</p>
          </div>
        )}
      </div>

      {selectedInvoice && (
        <div
          className="invoice-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invoice-detail-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedInvoice(null);
          }}
        >
          <div className="invoice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-modal-header">
              <h3 id="invoice-detail-title">Factura {selectedInvoice.invoiceNumber}</h3>
              <button
                type="button"
                className="invoice-modal-close"
                onClick={() => setSelectedInvoice(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="invoice-modal-body">
              {detailLoading && <p className="invoice-modal-loading">Cargando detalle…</p>}
              {detailError && (
                <p className="invoices-error-text">
                  {detailError?.response?.data?.message || detailError?.message || 'Error al cargar el detalle'}
                </p>
              )}
              {!detailLoading && !detailError && invoiceDetail && (
                <>
                  {invoiceDetail.status === 'voided' && (
                    <div className="invoice-void-banner">
                      <span className="status-badge status-voided">Anulada</span>
                      {invoiceDetail.voidReason && (
                        <p className="invoice-void-reason">
                          <strong>Motivo:</strong> {invoiceDetail.voidReason}
                        </p>
                      )}
                      {invoiceDetail.voidAt && (
                        <p className="invoice-void-meta">
                          {new Date(invoiceDetail.voidAt).toLocaleString('es-CO')}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="invoice-detail-grid">
                    <p><strong>Tercero:</strong> {invoiceDetail.thirdParty?.name || '—'}</p>
                    <p><strong>Estado operativo:</strong> {invoiceDetail.status || '—'}</p>
                    <p><strong>NIT:</strong> {invoiceDetail.thirdParty?.nit || '—'}</p>
                    <p><strong>Emisión:</strong> {invoiceDetail.issueDate ? new Date(invoiceDetail.issueDate).toLocaleDateString() : '—'}</p>
                    <p><strong>Total:</strong> ${formatMoney(invoiceDetail.total)}</p>
                    <p><strong>Estado DIAN:</strong> {invoiceDetail.dianStatus}</p>
                    {invoiceDetail.externalOrderNumber && (
                      <p><strong>Orden externa:</strong> {invoiceDetail.externalOrderNumber}</p>
                    )}
                  </div>
                  {(invoiceDetail.gatewayFee != null ||
                    invoiceDetail.gatewayFeeTax != null ||
                    invoiceDetail.gatewayNet != null) && (
                    <div className="invoice-gateway-section">
                      <h4>
                        Costos de pasarela
                        {invoiceDetail.gatewayName ? ` (${invoiceDetail.gatewayName})` : ''}
                      </h4>
                      <ul className="invoice-gateway-lines">
                        {invoiceDetail.gatewayFee != null && (
                          <li>
                            <span>Comisión</span>{' '}
                            <strong>${formatMoney(invoiceDetail.gatewayFee)}</strong>
                          </li>
                        )}
                        {invoiceDetail.gatewayFeeTax != null && (
                          <li>
                            <span>IVA comisión</span>{' '}
                            <strong>${formatMoney(invoiceDetail.gatewayFeeTax)}</strong>
                          </li>
                        )}
                        {invoiceDetail.gatewayNet != null && (
                          <li className="invoice-gateway-net">
                            <span>Neto recibido</span>{' '}
                            <strong>${formatMoney(invoiceDetail.gatewayNet)}</strong>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                  {invoiceDetail.lines && invoiceDetail.lines.length > 0 && (
                    <div className="invoice-lines-wrap">
                      <h4>Líneas</h4>
                      <table className="invoices-table invoice-detail-lines">
                        <thead>
                          <tr>
                            <th>Descripción</th>
                            <th>Cant.</th>
                            <th>P. unit</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceDetail.lines.map((line) => (
                            <tr key={line.id}>
                              <td>{line.description}</td>
                              <td>{line.quantity}</td>
                              <td>${formatMoney(line.unitPrice)}</td>
                              <td>${formatMoney(line.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {voidModalInvoice && (
        <div
          className="void-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="void-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !voidMutation.isLoading) {
              setVoidModalInvoice(null);
              setVoidReason('');
            }
          }}
        >
          <div className="void-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="void-modal-title">Anular factura {voidModalInvoice.invoiceNumber}</h3>
            <p className="void-modal-intro">
              Esta acción deja la factura como anulada en el sistema. Indica un motivo (obligatorio).
            </p>
            <label className="void-modal-label" htmlFor="void-reason">Motivo de anulación</label>
            <textarea
              id="void-reason"
              className="void-modal-textarea"
              rows={4}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Ej. Error en datos del cliente, duplicada, nota de crédito manual…"
            />
            <div className="void-modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  if (!voidMutation.isLoading) {
                    setVoidModalInvoice(null);
                    setVoidReason('');
                  }
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger-void"
                disabled={voidMutation.isLoading}
                onClick={() => {
                  const r = voidReason.trim();
                  if (!r) {
                    alert('El motivo de anulación es obligatorio');
                    return;
                  }
                  voidMutation.mutate({ id: voidModalInvoice.id, reason: r });
                }}
              >
                {voidMutation.isLoading ? 'Anulando…' : 'Confirmar anulación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;

